import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { VerifyDomainForm } from '@/components/domains/verify-form'
import { BulkVerifyForm } from '@/components/domains/bulk-verify-form'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { listBulkJobs } from '@/api/domains'
import { BulkJobSummary, getAccountAlias } from '@/lib/types'
import { Layers, RefreshCw } from 'lucide-react'

const statusConfig: Record<BulkJobSummary['status'], { label: string; className: string }> = {
  PENDING:    { label: 'Pending',    className: 'bg-amber-500/15 text-amber-700' },
  PROCESSING: { label: 'Processing', className: 'bg-blue-500/15 text-blue-700' },
  COMPLETED:  { label: 'Completed',  className: 'bg-teal-500/15 text-teal-700' },
  FAILED:     { label: 'Failed',     className: 'bg-rose-500/15 text-rose-700' },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString()
}

export function VerifyDomainPage() {
  const navigate = useNavigate()
  const [isBulkMode, setIsBulkMode] = useState(false)
  const [jobs, setJobs] = useState<BulkJobSummary[]>([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [jobsRefreshing, setJobsRefreshing] = useState(false)

  const fetchJobs = useCallback(async (options?: { refresh?: boolean }) => {
    if (options?.refresh) setJobsRefreshing(true)
    const result = await listBulkJobs()
    if (result.success && result.data) {
      setJobs(result.data)
    }
    setJobsLoading(false)
    setJobsRefreshing(false)
  }, [])

  useEffect(() => { fetchJobs() }, [fetchJobs])

  const activeJobs = jobs.filter((j) => j.status === 'PENDING' || j.status === 'PROCESSING')
  const recentJobs = jobs.slice(0, 20)

  return (
    <div className="space-y-6">
      {/* Mode Toggle */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={() => setIsBulkMode(!isBulkMode)}
          className="gap-2"
        >
          <Layers className="h-4 w-4" />
          {isBulkMode ? 'Single Domain' : 'Bulk Verify'}
        </Button>
      </div>

      {isBulkMode ? <BulkVerifyForm /> : <VerifyDomainForm />}

      {/* Jobs List */}
      {!jobsLoading && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                Recent Bulk Jobs
                {activeJobs.length > 0 && (
                  <Badge className="bg-blue-500/15 text-blue-700">{activeJobs.length} active</Badge>
                )}
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => fetchJobs({ refresh: true })}
                disabled={jobsRefreshing}
              >
                <RefreshCw className={`h-4 w-4 ${jobsRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className={recentJobs.length === 0 ? undefined : 'p-0'}>
            {recentJobs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No bulk jobs yet.</p>
            ) : (
              <div className="divide-y">
                {recentJobs.map((job) => {
                  const status = statusConfig[job.status]
                  return (
                    <div
                      key={job.job_id}
                      className="flex items-center justify-between px-6 py-3 hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/dashboard/domains/verify/jobs/${job.job_id}`)}
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <Badge className={status.className}>{status.label}</Badge>
                        <div className="min-w-0">
                          <div className="font-mono text-xs text-muted-foreground truncate">
                            {job.job_id}
                          </div>
                          <div className="text-sm">
                            {getAccountAlias(job.account_id)}
                            <span className="text-muted-foreground ml-2">{formatDate(job.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-right shrink-0 ml-4">
                        <div>
                          <span className="font-medium">{job.processed}</span>
                          <span className="text-muted-foreground">/{job.total}</span>
                        </div>
                        {(job.success_count > 0 || job.failure_count > 0) && (
                          <div className="flex gap-2">
                            <span className="text-teal-600">{job.success_count}✓</span>
                            {job.failure_count > 0 && (
                              <span className="text-rose-600">{job.failure_count}✗</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
